import { create } from "zustand";
import type { ILnp } from "../type";

interface EditState {
  // Map of PK -> diff payload (only fields the user touched)
  dirtyRows: Record<number, Partial<ILnp>>;
  // Snapshot of the original row at the moment a cell was first edited.
  originalRows: Record<number, ILnp>;
  // Last-known server version for each row (for optimistic-lock checks).
  versions: Record<number, number>;

  // Mark a single cell change. `original` is the row as it was server-side
  // before the user touched it (used for diff + ConflictModal display).
  setCell: <K extends keyof ILnp>(
    pk: number,
    field: K,
    value: ILnp[K],
    original: ILnp
  ) => void;

  // Drop pending changes for a single row.
  discard: (pk: number) => void;

  // Drop all pending changes.
  discardAll: () => void;

  // Replace dirty/version state for a row after a successful save.
  applySaved: (pk: number, saved: ILnp) => void;

  // Replace original + version when the server returned 409 conflict.
  replaceOriginalFromServer: (pk: number, serverRow: ILnp) => void;

  // Total dirty rows (used for sticky toolbar count).
  dirtyCount: () => number;

  // Number of dirty cells across all rows.
  dirtyCellCount: () => number;
}

const useEditStore = create<EditState>()((set, get) => ({
  dirtyRows: {},
  originalRows: {},
  versions: {},

  setCell: (pk, field, value, original) => {
    set((state) => {
      const wasOriginal = (original as ILnp)[field];
      const dirty = { ...state.dirtyRows };
      const originals = { ...state.originalRows };
      const versions = { ...state.versions };

      // Snapshot original row + version once per row (don't overwrite later edits).
      if (originals[pk] === undefined) {
        originals[pk] = original;
        if (typeof original.version === "number") {
          versions[pk] = original.version;
        }
      }

      const rowDirty = { ...(dirty[pk] ?? {}) } as Partial<ILnp>;
      if (value === wasOriginal) {
        // Reverting to original value clears the dirty mark for this field.
        delete (rowDirty as Record<string, unknown>)[field as string];
      } else {
        (rowDirty as Record<string, unknown>)[field as string] = value;
      }

      if (Object.keys(rowDirty).length === 0) {
        delete dirty[pk];
        delete originals[pk];
      } else {
        dirty[pk] = rowDirty;
      }

      return { dirtyRows: dirty, originalRows: originals, versions };
    });
  },

  discard: (pk) => {
    set((state) => {
      const dirty = { ...state.dirtyRows };
      const originals = { ...state.originalRows };
      delete dirty[pk];
      delete originals[pk];
      return { dirtyRows: dirty, originalRows: originals };
    });
  },

  discardAll: () => set({ dirtyRows: {}, originalRows: {}, versions: {} }),

  applySaved: (pk, saved) => {
    set((state) => {
      const dirty = { ...state.dirtyRows };
      const originals = { ...state.originalRows };
      const versions = { ...state.versions };
      delete dirty[pk];
      delete originals[pk];
      if (typeof saved.version === "number") {
        versions[pk] = saved.version;
      }
      return { dirtyRows: dirty, originalRows: originals, versions };
    });
  },

  replaceOriginalFromServer: (pk, serverRow) => {
    set((state) => {
      const originals = { ...state.originalRows, [pk]: serverRow };
      const versions = { ...state.versions };
      if (typeof serverRow.version === "number") {
        versions[pk] = serverRow.version;
      }
      return { originalRows: originals, versions };
    });
  },

  dirtyCount: () => Object.keys(get().dirtyRows).length,

  dirtyCellCount: () =>
    Object.values(get().dirtyRows).reduce(
      (sum, row) => sum + Object.keys(row).length,
      0
    ),
}));

export default useEditStore;
